import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CalendarDays,
  ChevronDown,
  Filter,
  Search,
  RotateCcw,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";

interface RewardPointInvoiceFiltersProps {
  onSearch: (term: string) => void;
  onFilterChange: (filters: any) => void;
  onDateRangeChange: (range: string) => void;
  onReset: () => void;
  programs?: any[];
  redemptionMerchants?: any[];
}

// Helper to get start/end date for quick-pick ranges
function getDateRangeMillis(range: string) {
  const now = new Date();
  let start, end;
  switch (range) {
    case "30days":
      start = new Date(now);
      start.setDate(now.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "7days":
      start = new Date(now);
      start.setDate(now.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "today":
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "yesterday":
      start = new Date(now);
      start.setDate(now.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setDate(now.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;
    case "thisMonth":
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "lastMonth":
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      break;
    default:
      start = null;
      end = null;
  }
  return {
    startDate: start ? start.toISOString() : undefined,
    endDate: end ? end.toISOString() : undefined,
  };
}

export { getDateRangeMillis };

export function RewardPointInvoiceFilters({
  onSearch,
  onFilterChange,
  onDateRangeChange,
  onReset,
  programs = [],
  redemptionMerchants = [],
}: RewardPointInvoiceFiltersProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("status");
  const [filterSelections, setFilterSelections] = useState<any>({
    status: "",
    program: "",
    redemptionMerchant: "",
  });
  const [pendingFilterSelections, setPendingFilterSelections] = useState<any>({
    status: "",
    program: "",
    redemptionMerchant: "",
    dateRange: "none",
  });
  const [searchInputs, setSearchInputs] = useState<any>({
    status: "",
    program: "",
    redemptionMerchant: "",
  });
  const [dateRange, setDateRange] = useState("none");
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined
  );

  const activeFilterCount = ["status", "program", "redemptionMerchant"].reduce(
    (acc, key) => {
      return acc + (filterSelections[key] ? 1 : 0);
    },
    0
  );

  useEffect(() => {
    if (filterPopoverOpen) {
      setPendingFilterSelections({ ...filterSelections });
    }
  }, [filterPopoverOpen]);

  const applyFilters = () => {
    setFilterSelections({ ...pendingFilterSelections });
    const filterPayload: any = { ...pendingFilterSelections };
    if (pendingFilterSelections.program) {
      filterPayload.programCode = pendingFilterSelections.program;
      delete filterPayload.program;
    }
    if (pendingFilterSelections.redemptionMerchant) {
      filterPayload.redemptionMerchantCode =
        pendingFilterSelections.redemptionMerchant;
    } else {
      filterPayload.redemptionMerchantCode = undefined;
    }
    delete filterPayload.redemptionMerchant;
    if (
      pendingFilterSelections.dateRange === "custom" &&
      pendingFilterSelections.startDate &&
      pendingFilterSelections.endDate
    ) {
      filterPayload.startDate = pendingFilterSelections.startDate;
      filterPayload.endDate = pendingFilterSelections.endDate;
    } else if (
      pendingFilterSelections.dateRange &&
      pendingFilterSelections.dateRange !== "none"
    ) {
      const { startDate, endDate } = getDateRangeMillis(
        pendingFilterSelections.dateRange
      );
      filterPayload.startDate = startDate;
      filterPayload.endDate = endDate;
    } else {
      filterPayload.startDate = undefined;
      filterPayload.endDate = undefined;
    }
    if (pendingFilterSelections.dateRange) {
      setDateRange(pendingFilterSelections.dateRange);
      onDateRangeChange(pendingFilterSelections.dateRange);
    }
    onFilterChange(filterPayload);
    setFilterPopoverOpen(false);
  };

  const handleOptionSelect = (category: string, value: string) => {
    setPendingFilterSelections((prev: any) => ({
      ...prev,
      [category]: prev[category] === value ? "" : value,
    }));
  };

  const handleSearchInput = (category: string, value: string) => {
    setSearchInputs((prev: any) => ({ ...prev, [category]: value }));
  };

  const handleClearAll = () => {
    setFilterSelections({ status: "", program: "", redemptionMerchant: "" });
    setPendingFilterSelections({
      status: "",
      program: "",
      redemptionMerchant: "",
      dateRange: "none",
      startDate: undefined,
      endDate: undefined,
    });
    setCustomDateRange(undefined);
    setSearchInputs({ status: "", program: "", redemptionMerchant: "" });
    setDateRange("none");
    onFilterChange({
      status: "",
      programCode: "",
      redemptionMerchantCode: "",
      dateRange: "none",
      startDate: undefined,
      endDate: undefined,
    });
  };

  const renderOptions = () => {
    switch (activeCategory) {
      case "status":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {["PAID", "PENDING", "OVERDUE"].map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="status-filter"
                  checked={pendingFilterSelections.status === opt}
                  onChange={() => handleOptionSelect("status", opt)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="text-xs font-medium">
                  {opt.charAt(0) + opt.slice(1).toLowerCase()}
                </span>
              </label>
            ))}
            {pendingFilterSelections.status && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("status", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "program":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {programs
              .filter((p: any) =>
                p.programName
                  .toLowerCase()
                  .includes((searchInputs.program || "").toLowerCase())
              )
              .map((program: any) => (
                <label
                  key={program.programCode}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="program-filter"
                    checked={
                      pendingFilterSelections.program === program.programCode
                    }
                    onChange={() =>
                      handleOptionSelect("program", program.programCode)
                    }
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="text-xs font-medium">
                    {program.programName}
                  </span>
                </label>
              ))}
            {pendingFilterSelections.program && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("program", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "redemptionMerchant":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {redemptionMerchants
              .filter((m: any) =>
                m.merchantName
                  .toLowerCase()
                  .includes(
                    (searchInputs.redemptionMerchant || "").toLowerCase()
                  )
              )
              .map((merchant: any) => (
                <label
                  key={merchant.merchantCode}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="redemption-merchant-filter"
                    checked={
                      pendingFilterSelections.redemptionMerchant ===
                      merchant.merchantCode
                    }
                    onChange={() =>
                      handleOptionSelect(
                        "redemptionMerchant",
                        merchant.merchantCode
                      )
                    }
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="text-xs font-medium">
                    {merchant.merchantName}
                  </span>
                </label>
              ))}
            {pendingFilterSelections.redemptionMerchant && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("redemptionMerchant", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "date":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            <Select
              value={pendingFilterSelections.dateRange || "none"}
              onValueChange={(v) =>
                setPendingFilterSelections((prev) => ({
                  ...prev,
                  dateRange: v,
                }))
              }
            >
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder="Choose Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Choose Date</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="thisMonth">This Month</SelectItem>
                <SelectItem value="lastMonth">Last Month</SelectItem>
              </SelectContent>
            </Select>
            {pendingFilterSelections.dateRange === "custom" && (
              <div className="mt-2">
                <DatePickerWithRange
                  date={customDateRange}
                  setDate={(range) => {
                    setCustomDateRange(range);
                    if (range?.from && range?.to) {
                      const startDate = range.from.toISOString();
                      const endDate = range.to.toISOString();
                      setFilterSelections((prev) => ({
                        ...prev,
                        startDate,
                        endDate,
                        dateRange: "custom",
                      }));
                      setPendingFilterSelections((prev) => ({
                        ...prev,
                        startDate,
                        endDate,
                        dateRange: "custom",
                      }));
                      setDateRange("custom");
                      onDateRangeChange("custom");
                      onFilterChange({
                        ...filterSelections,
                        startDate,
                        endDate,
                        dateRange: "custom",
                      });
                    }
                  }}
                  placeholder="Custom Date Range"
                />
              </div>
            )}
            {pendingFilterSelections.dateRange &&
              pendingFilterSelections.dateRange !== "none" && (
                <button
                  className="text-xs text-gray-500 hover:underline mt-2 self-start"
                  onClick={() =>
                    setPendingFilterSelections((prev) => ({
                      ...prev,
                      dateRange: "none",
                      startDate: undefined,
                      endDate: undefined,
                    }))
                  }
                  type="button"
                >
                  Clear
                </button>
              )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="mb-6 border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-row flex-wrap items-center gap-3 w-full">
          <div className="w-64 h-10 flex items-center">
            <DatePickerWithRange
              date={customDateRange}
              setDate={(range) => {
                setCustomDateRange(range);
                if (range?.from && range?.to) {
                  const startDate = range.from.toISOString();
                  const endDate = range.to.toISOString();
                  setFilterSelections((prev) => ({
                    ...prev,
                    startDate,
                    endDate,
                    dateRange: "custom",
                  }));
                  setPendingFilterSelections((prev) => ({
                    ...prev,
                    startDate,
                    endDate,
                    dateRange: "custom",
                  }));
                  setDateRange("custom");
                  onDateRangeChange("custom");
                  onFilterChange({
                    ...filterSelections,
                    startDate,
                    endDate,
                    dateRange: "custom",
                  });
                }
              }}
              placeholder="Custom Date Range"
            />
          </div>
          <div className="relative grow max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <input
              placeholder="Search by Invoice ID"
              className="pl-9 h-10 w-full border rounded"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onSearch(e.target.value);
              }}
            />
          </div>
          <div className="flex gap-2 items-center ml-auto">
            <Popover
              open={filterPopoverOpen}
              onOpenChange={setFilterPopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant={activeFilterCount > 0 ? "default" : "outline"}
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                  onClick={() => setFilterPopoverOpen(true)}
                  size="sm"
                >
                  <Filter className="h-4 w-4" />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[340px] p-0"
                align="start"
                side="left"
              >
                <div className="flex max-h-[320px] h-[320px]">
                  <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                    {[
                      { label: "Status", value: "status" },
                      { label: "Program", value: "program" },
                      {
                        label: "Redemption Merchant",
                        value: "redemptionMerchant",
                      },
                      { label: "Date", value: "date" },
                    ].map((cat) => (
                      <button
                        key={cat.value}
                        className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${
                          activeCategory === cat.value
                            ? "bg-white shadow text-blue-600"
                            : "text-gray-700"
                        }`}
                        onClick={() => setActiveCategory(cat.value)}
                      >
                        {cat.label}
                      </button>
                    ))}
                    <div className="flex-1" />
                    <button
                      className="text-[10px] text-gray-500 hover:underline mt-2"
                      onClick={handleClearAll}
                      type="button"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="flex-1 p-2 flex flex-col min-h-0">
                    <div className="flex items-center mb-1">
                      {activeCategory !== "status" && (
                        <input
                          placeholder={`Search ${
                            activeCategory === "redemptionMerchant"
                              ? "Redemption Merchant"
                              : activeCategory.charAt(0).toUpperCase() +
                                activeCategory.slice(1)
                          }`}
                          value={searchInputs[activeCategory] || ""}
                          onChange={(e) =>
                            handleSearchInput(activeCategory, e.target.value)
                          }
                          className="h-7 w-full text-xs border rounded px-2"
                        />
                      )}
                    </div>
                    <div className="text-xs font-medium flex-1 min-h-0 overflow-y-auto gap-1 flex flex-col">
                      {renderOptions()}
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        size="sm"
                        className="text-xs px-3 py-1 h-7"
                        onClick={applyFilters}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 px-3 py-2 text-sm"
              onClick={() => {
                setSearchTerm("");
                setDateRange("none");
                setFilterSelections({
                  status: "",
                  program: "",
                  redemptionMerchant: "",
                });
                setPendingFilterSelections({
                  status: "",
                  program: "",
                  redemptionMerchant: "",
                  dateRange: "none",
                });
                setSearchInputs({
                  status: "",
                  program: "",
                  redemptionMerchant: "",
                });
                onReset();
              }}
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
